import React, { useState, useEffect } from 'react';
import { Box, Typography, Tab, Tabs, useTheme, useMediaQuery } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

// Ad serving in LLM responses component
const AdInResponsePreview = () => {
  // Using state just to control animations
  const [showResponse, setShowResponse] = useState(false);
  const [showAd, setShowAd] = useState(false);
  
  const userQuery = "What are the best headphones for working out?";
  
  const responseText = "Based on durability, sound quality, and fit, the best workout headphones are:\n\n1. Sony WF-1000XM4 - Excellent noise cancellation with sweat resistance\n2. Jabra Elite Active 75t - Secure fit with IP57 water/dust resistance\n3. Beats Fit Pro - Apple integration with secure wing tips for active use\n\n";
  
  // Show the completed response and ad immediately with a slight animation delay
  useEffect(() => {
    const responseTimeout = setTimeout(() => {
      setShowResponse(true);
    }, 500);
    
    const adTimeout = setTimeout(() => {
      setShowAd(true);
    }, 1500);
    
    return () => {
      clearTimeout(responseTimeout);
      clearTimeout(adTimeout);
    };
  }, []);

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <Box sx={{ 
        p: 2, 
        borderRadius: '12px 12px 0 0',
        background: 'rgba(0, 0, 0, 0.2)', 
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <Typography variant="subtitle2" sx={{ color: '#94A3B8' }}>
          AI Assistant Chat
        </Typography>
      </Box>
      
      <Box sx={{ 
        flex: 1, 
        p: 3, 
        display: 'flex', 
        flexDirection: 'column',
        gap: 2,
        overflowY: 'auto'
      }}>
        {/* User query - always visible */}
        <Box sx={{ display: 'flex', mb: 2 }}>
          <Box sx={{ 
            backgroundColor: 'rgba(59, 130, 246, 0.1)', 
            p: 2, 
            borderRadius: '16px 16px 16px 0',
            maxWidth: '80%'
          }}>
            <Typography variant="body1">{userQuery}</Typography>
          </Box>
        </Box>
        
        {/* AI response with sponsored content */}
        <AnimatePresence>
          {showResponse && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end' 
              }}>
                <Box sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.08)', 
                  p: 2, 
                  borderRadius: '16px 16px 0 16px',
                  maxWidth: '85%'
                }}>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {responseText}
                  </Typography>
                  
                  {/* Sponsored message */}
                  {showAd && (
                    <motion.div
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Box 
                        component="span" 
                        sx={{ 
                          display: 'block',
                          color: '#e2e8f0', 
                          borderLeft: '3px solid #3B82F6',
                          paddingLeft: 1.5,
                          marginTop: 2,
                          marginBottom: 0.5,
                          position: 'relative',
                          background: 'linear-gradient(90deg, rgba(59, 130, 246, 0.1), transparent)',
                          py: 1,
                          borderRadius: '4px',
                        }}
                      >
                        <Box 
                          component="span" 
                          sx={{ 
                            color: '#3B82F6',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            position: 'absolute',
                            top: '-10px',
                            left: '12px',
                            background: 'rgba(15, 23, 42, 0.7)',
                            px: 0.8,
                            py: 0.2,
                            borderRadius: '4px',
                          }}
                        >
                          SPONSORED
                        </Box>
                        🎧 Jabra Elite Active 75t are 30% off this week at TechGear! Use code WORKOUT30 →
                      </Box>
                    </motion.div>
                  )}
                </Box>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
      </Box>
    </Box>
  );
};

// LLM Ad Strategist preview component
const AdStrategistPreview = () => {
  const [currentPhase, setCurrentPhase] = useState(0);
  const phases = [
    { title: "1. Audience Analysis", percentage: 0 },
    { title: "2. Content Matching", percentage: 33 },
    { title: "3. Ad Strategy Creation", percentage: 66 },
    { title: "4. Strategy Complete", percentage: 100 }
  ];
  
  const adStrategy = [
    {
      title: "Targeted Demographics",
      content: "Tech-savvy professionals, 25-45, interested in productivity tools"
    },
    {
      title: "Recommended Ad Format",
      content: "Native in-message ads with productivity app discount codes"
    },
    {
      title: "Optimal Placement",
      content: "After task completion recommendations or within productivity tips"
    },
    {
      title: "Performance Prediction",
      content: "Est. 3.2% CTR (42% above industry average)"
    }
  ];

  useEffect(() => {
    if (currentPhase < phases.length - 1) {
      const timeout = setTimeout(() => {
        setCurrentPhase(currentPhase + 1);
      }, 1200);
      return () => clearTimeout(timeout);
    }
  }, [currentPhase]);

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <Box sx={{ 
        p: 2, 
        borderRadius: '12px 12px 0 0',
        background: 'rgba(0, 0, 0, 0.2)', 
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="subtitle2" sx={{ color: '#94A3B8' }}>
          LLM Ad Strategist
        </Typography>
        <Typography variant="caption" sx={{ color: '#64748B' }}>
          Campaign: Productivity Suite Launch
        </Typography>
      </Box>
      
      <Box sx={{ 
        p: 3, 
        display: 'flex', 
        flexDirection: 'column', 
        gap: 3,
        flex: 1,
        overflowY: 'auto'
      }}>
        {/* Progress indicator */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ color: '#94A3B8' }}>
              {phases[currentPhase].title}
            </Typography>
            <Typography variant="body2" sx={{ color: '#94A3B8' }}>
              {phases[currentPhase].percentage}%
            </Typography>
          </Box>
          <Box sx={{ 
            width: '100%', 
            height: '8px', 
            borderRadius: '4px',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            overflow: 'hidden'
          }}>
            <motion.div
              initial={{ width: `${phases[Math.max(0, currentPhase - 1)].percentage}%` }}
              animate={{ width: `${phases[currentPhase].percentage}%` }}
              transition={{ duration: 0.8, ease: "easeInOut" }}
              style={{
                height: '100%',
                borderRadius: '4px',
                background: 'linear-gradient(90deg, #3B82F6, #2563EB)'
              }}
            />
          </Box>
        </Box>
        
        {/* Strategy content */}
        <AnimatePresence>
          {currentPhase === 3 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                {adStrategy.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.15 }}
                  >
                    <Box sx={{ 
                      p: 2,
                      borderRadius: '12px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      height: '100%'
                    }}>
                      <Typography variant="subtitle2" sx={{ color: '#94A3B8', mb: 1 }}>
                        {item.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#e2e8f0' }}>
                        {item.content}
                      </Typography>
                    </Box>
                  </motion.div>
                ))}
              </Box>
              
              <Box sx={{ 
                mt: 3,
                p: 2,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.1) 100%)',
                border: '1px solid rgba(59, 130, 246, 0.3)',
              }}>
                <Typography variant="subtitle2" sx={{ color: '#94A3B8', mb: 1 }}>
                  AI Recommendation
                </Typography>
                <Typography variant="body2" sx={{ color: '#e2e8f0' }}>
                  Deploy weekday afternoon ads (2-5pm) targeting project management queries with subtle productivity enhancement messaging.
                </Typography>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
        
        {currentPhase < 3 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                border: '3px solid rgba(59, 130, 246, 0.1)',
                borderTop: '3px solid #3B82F6',
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// Analytics Dashboard Preview component
const AnalyticsPreview = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [currentMetric, setCurrentMetric] = useState(0);
  
  // Mock metrics data
  const metrics = [
    { label: "Click-Through Rate", value: "3.8%", change: "+0.6%", positive: true },
    { label: "Conversion Rate", value: "2.1%", change: "+0.3%", positive: true },
    { label: "Ad Revenue", value: "$4,285", change: "+12%", positive: true },
    { label: "User Satisfaction", value: "91%", change: "-2%", positive: false }
  ];
  
  // Mock chart data points
  const chartData = [35, 42, 38, 45, 40, 52, 58, 55, 60, 62, 68, 70];
  
  useEffect(() => {
    // Simulate loading delay
    const loadTimeout = setTimeout(() => {
      setIsLoaded(true);
    }, 800);
    
    // Cycle through metrics
    if (isLoaded) {
      const metricInterval = setInterval(() => {
        setCurrentMetric((prev) => (prev + 1) % metrics.length);
      }, 2500);
      
      return () => clearInterval(metricInterval);
    }
    
    return () => clearTimeout(loadTimeout);
  }, [isLoaded, metrics.length]);

  return (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden'
    }}>
      <Box sx={{ 
        p: 2, 
        borderRadius: '12px 12px 0 0',
        background: 'rgba(0, 0, 0, 0.2)', 
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="subtitle2" sx={{ color: '#94A3B8' }}>
          Ad Performance Dashboard
        </Typography>
        <Typography variant="caption" sx={{ color: '#64748B' }}>
          Live Data • Last 30 Days
        </Typography>
      </Box>
      
      {isLoaded ? (
        <Box sx={{ 
          p: 3, 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 3,
          flex: 1,
          overflowY: 'auto'
        }}>
          {/* Featured metric */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentMetric}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
            >
              <Box sx={{ 
                p: 3, 
                borderRadius: '16px',
                background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.15) 0%, rgba(59, 130, 246, 0.05) 100%)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                textAlign: 'center'
              }}>
                <Typography variant="body2" sx={{ color: '#94A3B8', mb: 1 }}>
                  {metrics[currentMetric].label}
                </Typography>
                <Typography variant="h3" sx={{ 
                  color: 'white', 
                  fontWeight: 700,
                  fontSize: { xs: '2rem', sm: '2.5rem' },
                  mb: 1
                }}>
                  {metrics[currentMetric].value}
                </Typography>
                <Box sx={{ 
                  display: 'inline-flex', 
                  alignItems: 'center',
                  color: metrics[currentMetric].positive ? '#10B981' : '#EF4444',
                  fontSize: '0.875rem'
                }}>
                  {metrics[currentMetric].positive ? '↑' : '↓'} {metrics[currentMetric].change} from last month
                </Box>
              </Box>
            </motion.div>
          </AnimatePresence>
          
          {/* Chart */}
          <Box sx={{ flex: 1, mt: 1 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              mb: 1
            }}>
              <Typography variant="body2" sx={{ color: '#94A3B8' }}>
                Ad Revenue Trend
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748B' }}>
                Monthly
              </Typography>
            </Box>
            
            <Box sx={{ 
              height: '120px', 
              display: 'flex', 
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: 1,
              px: 1
            }}>
              {chartData.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ height: 0 }}
                  animate={{ height: `${value}%` }}
                  transition={{ 
                    duration: 0.5,
                    delay: index * 0.05,
                    ease: "easeOut"
                  }}
                  style={{
                    flex: 1,
                    borderRadius: '4px 4px 0 0',
                    background: `linear-gradient(to top, #3B82F6, ${index > 8 ? '#10B981' : '#3B82F6'})`,
                    maxWidth: '14px'
                  }}
                />
              ))}
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              mt: 1,
              color: '#64748B',
              fontSize: '0.75rem',
              px: 1
            }}>
              <span>Jan</span>
              <span>Mar</span>
              <span>Jun</span>
              <span>Sep</span>
              <span>Dec</span>
            </Box>
          </Box>
          
          {/* Live stats */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: 2,
            mt: 1
          }}>
            <Box sx={{ 
              p: 2,
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}>
              <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                Audience Engagement
              </Typography>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
                87%
              </Typography>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: '87%' }}
                transition={{ duration: 1, delay: 0.5 }}
                style={{
                  height: '4px',
                  background: 'linear-gradient(90deg, #3B82F6, #10B981)',
                  borderRadius: '2px',
                  marginTop: '8px'
                }}
              />
            </Box>
            
            <Box sx={{ 
              p: 2,
              borderRadius: '12px',
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}>
              <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                Live Ad Impressions
              </Typography>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                <motion.span
                  animate={{ opacity: [1, 0.5, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                  style={{ 
                    width: '8px', 
                    height: '8px',
                    borderRadius: '50%',
                    background: '#10B981',
                    display: 'inline-block',
                    marginRight: '8px'
                  }}
                />
                268.4K
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: 1 }}>
                +124 in last minute
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              border: '3px solid rgba(59, 130, 246, 0.1)',
              borderTop: '3px solid #3B82F6',
            }}
          />
        </Box>
      )}
    </Box>
  );
};

// Main component that combines previews with tabs
const LandingPreviews = () => {
  // Always start with tab 0 (Ad-Serving) selected
  const [tabValue, setTabValue] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Reset to Ad-Serving tab when component mounts
  useEffect(() => {
    setTabValue(0);
  }, []);

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    }}>
      <Tabs 
        value={tabValue} 
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{ 
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          '& .MuiTab-root': {
            color: 'rgba(255, 255, 255, 0.6)',
            fontWeight: 500,
            textTransform: 'none',
            fontSize: isMobile ? '0.7rem' : '0.875rem',
            minWidth: 0,
            padding: isMobile ? '6px 8px' : undefined,
          },
          '& .Mui-selected': {
            color: 'white',
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#3B82F6',
          }
        }}
      >
        <Tab label="Ad-Serving" />
        <Tab label="Ad Strategist" />
        <Tab label="Analytics" />
      </Tabs>
      
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={tabValue}
            initial={{ opacity: 0, x: 20 * (tabValue - 1) }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
          >
            {tabValue === 0 ? (
              <AdInResponsePreview />
            ) : tabValue === 1 ? (
              <AdStrategistPreview />
            ) : (
              <AnalyticsPreview />
            )}
          </motion.div>
        </AnimatePresence>
      </Box>
    </Box>
  );
};

export default LandingPreviews; 